import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Modal from '../common/Modal';
import { aboutUsApi } from '../../services/api';
import { toast } from 'react-hot-toast';

const AboutUsSection: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aboutContent, setAboutContent] = useState({
    title: 'About Our Company',
    description: 'Welcome to our admin dashboard. This is where you can manage all aspects of your application.',
    bottomHeading1: 'Our Mission',
    bottomParagraph1: 'Our mission is to provide the best service possible.',
    bottomHeading2: 'Our Vision',
    bottomParagraph2: 'We envision a future where technology makes life easier.',
    bottomHeading3: 'Our Values',
    bottomParagraph3: 'Integrity, Innovation, and Excellence guide everything we do.',
    howWeWork: 'We work collaboratively with our clients to achieve outstanding results.'
  });

  // Load content on component mount
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await aboutUsApi.getContent();
        setAboutContent(data);
      } catch (error) {
        console.error('Error fetching about us content:', error);
        toast.error('Failed to load about us content');
      }
    };

    fetchContent();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await aboutUsApi.updateContent(aboutContent);
      toast.success('About us content updated successfully', { duration: 3000 });
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating about us content:', error);
      toast.error('Failed to update about us content', { duration: 3000 });
    }
  };

  return (
    <div className="px-4 py-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">About Us</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-center"
        >
          Edit Content
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-800">{aboutContent.title}</h3>
          <p className="text-sm sm:text-base text-gray-600 whitespace-pre-line">{aboutContent.description}</p>
        </div>
        
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <h4 className="text-base sm:text-lg font-semibold mb-2 text-primary">{aboutContent.bottomHeading1}</h4>
            <p className="text-sm sm:text-base text-gray-600">{aboutContent.bottomParagraph1}</p>
          </div>
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <h4 className="text-base sm:text-lg font-semibold mb-2 text-primary">{aboutContent.bottomHeading2}</h4>
            <p className="text-sm sm:text-base text-gray-600">{aboutContent.bottomParagraph2}</p>
          </div>
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <h4 className="text-base sm:text-lg font-semibold mb-2 text-primary">{aboutContent.bottomHeading3}</h4>
            <p className="text-sm sm:text-base text-gray-600">{aboutContent.bottomParagraph3}</p>
          </div>
        </div>

        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <h4 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">How We Work</h4>
          <p className="text-sm sm:text-base text-gray-600">{aboutContent.howWeWork}</p>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Edit About Us Content"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Main Heading</label>
                <input
                  type="text"
                  value={aboutContent.title}
                  onChange={(e) => setAboutContent({...aboutContent, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter main heading"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Main Paragraph</label>
                <textarea
                  rows={4}
                  value={aboutContent.description}
                  onChange={(e) => setAboutContent({...aboutContent, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter main paragraph"
                  required
                ></textarea>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 mt-4">Bottom Section 1</h4>
                  <input
                    type="text"
                    value={aboutContent.bottomHeading1}
                    onChange={(e) => setAboutContent({...aboutContent, bottomHeading1: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Heading 1"
                  />
                  <textarea
                    rows={3}
                    value={aboutContent.bottomParagraph1}
                    onChange={(e) => setAboutContent({...aboutContent, bottomParagraph1: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Paragraph 1"
                  ></textarea>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 mt-4">Bottom Section 2</h4>
                  <input
                    type="text"
                    value={aboutContent.bottomHeading2}
                    onChange={(e) => setAboutContent({...aboutContent, bottomHeading2: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Heading 2"
                  />
                  <textarea
                    rows={3}
                    value={aboutContent.bottomParagraph2}
                    onChange={(e) => setAboutContent({...aboutContent, bottomParagraph2: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Paragraph 2"
                  ></textarea>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700">Bottom Section 3</h4>
                  <input
                    type="text"
                    value={aboutContent.bottomHeading3}
                    onChange={(e) => setAboutContent({...aboutContent, bottomHeading3: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Heading 3"
                  />
                  <textarea
                    rows={3}
                    value={aboutContent.bottomParagraph3}
                    onChange={(e) => setAboutContent({...aboutContent, bottomParagraph3: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Paragraph 3"
                  ></textarea>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700">How We Work</h4>
                  <textarea
                    rows={3}
                    value={aboutContent.howWeWork}
                    onChange={(e) => setAboutContent({...aboutContent, howWeWork: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="How we work paragraph"
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
              >
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AboutUsSection;
